import { type JSX,MouseEventHandler, useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
    GridCallbackDetails,
    GridFeatureMode,
    GridPaginationModel,
    GridRenderCellParams,
    GridRowId,
    GridRowModel,
    GridRowSpacingParams,
    GridSortModel } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setDialogOpen } from 'common/components/deleteDialog/deleteDialogSlice';
import { openDrawer, setCurrentDrawerId, setCurrentEntity, setCurrentOperation, setSaveButtonEnabled } from 'common/components/EditDrawer/editDrawerSlice';
import { FilterProps } from 'common/components/filter/FilterBar.types';
import { GridBar } from 'common/components/GridBar/GridBar';
import { PaginationMode } from 'common/constants/paginationMode';
import { ErrorBoundary } from 'common/hocs/ErrorBoundary';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { ActionDefinition, ColumnDefinition, ColumnType, DeleteDialogContentProps, DrawerEntitiesNames, EditDrawerContentProps, FetchExportDataFunction, FetchExportDataResult,Operations } from 'common/types/TypeDefs';
import { getActionColumn, getChipColumn, getChipsColumn, getDateTimeTooltipColumn,getFirstColumn, getTooltipColumn } from 'common/utils/ColumnGenerator';
import { handleExportDataFetchError } from 'common/utils/errorHandlers';
import { isFilterApplied } from 'common/utils/filterHelpers';
import { processFetchingExportData } from 'common/utils/getExportDataHelper';
import { useSnackbar } from 'notistack';

import { ExportDialog } from '../exportDialog/ExportDialog';
import { setExportDialogOpen } from '../exportDialog/exportDialogSlice';

import { CustomPagination } from './CustomPagination';
import { selectFilteredRows, selectPaginationGridData, selectPaginationModel, setPaginationGridData } from './palmDataGridSlice';
import { StyledDataGrid } from './StyledDataGrid';

type PalmDataGridProps<T, K> = {
    contentFunction?: (props: EditDrawerContentProps<T>) => JSX.Element | null;
    deleteFunction?: (props: DeleteDialogContentProps<K>) => JSX.Element;
    getRowLink?: (id: string) => string;
    actionDefinitions?: ActionDefinition[];
    columnDefinitions?: ColumnDefinition[];
    title: string;
    operationName?: Operations;
    isCreateButtonDisabled?: boolean;
    filter?: FilterProps;
    processing: boolean;
    shouldShowCreateButton?: boolean;
    paginationMode?: GridFeatureMode;
    searchFilterDisabled?: boolean;
    paginationInfo?: {
            take?: number;
            handlePageChange?: (paginationModel: GridPaginationModel) => void;
            handleSortModelChange?: (model: GridSortModel, details: GridCallbackDetails) => void;
        };
    refreshDataGridClick?: MouseEventHandler<HTMLButtonElement>;
    customDeleteTitle?: string;
    currentEntityName?: DrawerEntitiesNames;
    clientSortHandler?: (model: GridSortModel, details: GridCallbackDetails) => void;
    sortModel?: GridSortModel;
    fetchExportData?: FetchExportDataFunction;
    shouldShowExportButton?: boolean;
    customCreateButtonName?: string;
};

/*
    Generic type T represents the data of an editing row (unknown by default).
    Generic type K represents the data of a deleting row (unknown by default).

    These rows are passed "as is" to the corresponding content callbacks.
    These callbacks should be typed inside the specific component (ex: TenantDeleteDialogContent.tsx).
    Example of typing inside the specific component:
        editingRow: DeleteDialogContentProps<{ name: string }>
    where "{ name: string }" is a required field from the deleting row.
*/
export function PalmDataGrid<T, K>({
    contentFunction,
    deleteFunction,
    getRowLink,
    actionDefinitions,
    columnDefinitions,
    title,
    operationName = Operations.Create,
    isCreateButtonDisabled,
    filter,
    processing,
    shouldShowCreateButton = true,
    searchFilterDisabled,
    paginationMode = PaginationMode.Client,
    paginationInfo,
    refreshDataGridClick,
    customDeleteTitle,
    currentEntityName,
    clientSortHandler,
    sortModel = [],
    shouldShowExportButton = false,
    fetchExportData,
    customCreateButtonName
}: Readonly<PalmDataGridProps<T, K>>): JSX.Element {
    const { enqueueSnackbar } = useSnackbar();
    const paginationGridData = useAppSelector(selectPaginationGridData);
    const filteredRows = useAppSelector(selectFilteredRows(columnDefinitions, filter?.filters));
    const { rowsCount = 0 } = paginationGridData ?? {};
    const { take, handlePageChange, handleSortModelChange } = paginationInfo ?? {};
    const paginationModel = useAppSelector(selectPaginationModel);
    const [editingRow, setEditingRow] = useState<T | K>();
    const [abortController, setAbortController] = useState<AbortController>();
    const [editingItemId, setEditingItemId] = useState<GridRowId>('');
    const sortHandler = paginationMode === PaginationMode.Client ? clientSortHandler : handleSortModelChange;
    const navigate = useNavigate();

    const drawerId = title + 'DrawerId';

    const dispatch = useAppDispatch();
    const rows = paginationMode === PaginationMode.Client ?
        filteredRows :
        paginationGridData?.rows ?? [];

    const actionCallback = useCallback(
        (id: GridRowId, row: GridRowModel, rowAction: ActionDefinition) => () => {
            if (typeof rowAction.disabled === 'function' ? rowAction.disabled(row) : rowAction.disabled) {
                return;
            }
            setEditingItemId(id);
            setEditingRow(row as T | K);

            switch ( rowAction.action ) {
                case Operations.Edit:
                    dispatch(setCurrentDrawerId(drawerId));
                    dispatch(openDrawer());
                    dispatch(setCurrentOperation(rowAction.action));
                    dispatch(setSaveButtonEnabled(true));
                    break;
                case Operations.Delete:
                    dispatch(setDialogOpen(true));
                    break;
            }
        },
        [dispatch, drawerId, getRowLink, navigate]
    );

    const createCallback = useCallback(() => {
        if (isCreateButtonDisabled || !shouldShowCreateButton) {
            return;
        }
        if (currentEntityName) {
            dispatch(setCurrentEntity(currentEntityName));
        }
        dispatch(setCurrentOperation(operationName));
        dispatch(setCurrentDrawerId(drawerId));
        dispatch(openDrawer());
    }, [currentEntityName, dispatch, drawerId, isCreateButtonDisabled, operationName, shouldShowCreateButton]);

    const exportDataFetch = async (): Promise<FetchExportDataResult>  => {
        const controller = new AbortController();
        setAbortController(controller);
        const signal = controller.signal;
        try {
            return await processFetchingExportData(signal, paginationMode, filter, paginationGridData, fetchExportData);
        } catch(error) {
            if (!signal.aborted)
            {
                controller?.abort();
                dispatch(setPaginationGridData({ ...paginationGridData, paginationMode: PaginationMode.Server }));
                dispatch(setExportDialogOpen(false));
                const errorMessage = error instanceof Error ? error.message : '';
                handleExportDataFetchError(extractStatusCode(errorMessage), title, enqueueSnackbar);
            }

            return {
                isSuccessful: false,
                isClientSidePagination: false
            };
        }
    };

    const cancelExportCallback = () => {
        abortController?.abort();
    };

    const filterIsApplied = isFilterApplied(filter?.filters);
    const columns = columnDefinitions?.map((colDef) => {
        switch (colDef.columnType) {
            case ColumnType.First:
                return getFirstColumn(
                    colDef,
                    filterIsApplied,
                    rows || [],
                    paginationMode === PaginationMode.Server ? rowsCount : undefined
                );
            case ColumnType.Chip:
                return getChipColumn(colDef);
            case ColumnType.Chips:
                return getChipsColumn(colDef);
            case ColumnType.Action:
                return getActionColumn(colDef, actionDefinitions ?? [], actionCallback);
            case ColumnType.DatetimeTooltip:
                return getDateTimeTooltipColumn(colDef);
            default:
                return getTooltipColumn(colDef);
        }
    }) ?? [];

    const DATA_GRID_ROW_CURSOR_INLINE_STYLES = getRowLink && {
        '& .table-cell-truncate': { cursor: 'pointer' }
    };

    const modifiedColumns = columns.map((column) => {
        if (column.type === 'actions') {
            return column;
        }

        return {
            ...column,
            renderCell: (params: GridRenderCellParams) => {
                const originalContent = column.renderCell ? column.renderCell(params) : params.value;

                return getRowLink ? (
                    <Link
                        to={getRowLink(params.id.toString())}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                        {originalContent}
                    </Link>
                ) : (
                    originalContent
                );
            }
        };
    });

    return (
        <ErrorBoundary>
            <StyledDataGrid
                initialState={{
                    pagination: {
                        paginationModel: { pageSize: take ?? 10, page: 0 },
                    },
                }}
                getRowSpacing={(params: GridRowSpacingParams) => ({
                    top: params.isFirstVisible ? 2 : 3,
                    bottom: params.isLastVisible ? 1 : 0
                })}
                loading={processing}
                paginationModel={paginationMode === PaginationMode.Server ? paginationModel : undefined}
                columnHeaderHeight={42}
                pageSizeOptions={[20, 50, 100]}
                rows={processing ? [] : rows || []}
                columns={modifiedColumns}
                autoHeight
                disableColumnMenu={true}
                slots={{
                    pagination: CustomPagination,
                    toolbar: GridBar,
                }}
                slotProps={{ toolbar: {
                    createButtonName: `+ ${customCreateButtonName || `${operationName} ${title}`}`,
                    createButtonOnClick: createCallback,
                    shouldShowCreateButton: shouldShowCreateButton,
                    isDisabled: isCreateButtonDisabled,
                    filter: filter,
                    filterBarDisabled: processing,
                    searchFilterDisabled: searchFilterDisabled,
                    refreshButtonOnClick: refreshDataGridClick,
                    shouldShowExportButton: shouldShowExportButton,
                    paginationMode: paginationMode,
                    fetchExportData: exportDataFetch,
                    title: `${title} Details`
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any }}
                sx={DATA_GRID_ROW_CURSOR_INLINE_STYLES}
                sortingMode={paginationMode}
                onSortModelChange={sortHandler}
                sortModel={sortModel}
                paginationMode={paginationMode}
                rowCount={paginationMode === PaginationMode.Server ? rowsCount : undefined}
                onPaginationModelChange={paginationMode === PaginationMode.Server ? handlePageChange: undefined}
            />
            {contentFunction?.({
                title,
                editingItemInfo: {
                    editingItemId,
                    editingRow: editingRow as T,
                },
            })}
            {deleteFunction?.({
                title,
                customDeleteTitle,
                itemId: editingItemId,
                editingRow: editingRow as K,
                paginationInfo: paginationMode === PaginationMode.Server ?
                    {
                        rowsOnPage: rows.length,
                    } :
                    undefined
            })}
            <ExportDialog cancelExportCallback={cancelExportCallback} />
        </ErrorBoundary>
    );
}
